"use client";

import { useEffect, useRef } from "react";
import {
  motion,
  useAnimate,
  useInView,
  useReducedMotion,
} from "motion/react";

export type Testimonial = {
  text: string;
  image?: string;
  name: string;
  role: string;
};

export type TestimonialsColumnProps = {
  className?: string;
  testimonials: readonly Testimonial[];
  duration?: number;
  paused?: boolean;
};

type PlaybackControls = {
  cancel: () => void;
  pause: () => void;
  play: () => void;
};

function profileMark(name: string) {
  const numericSuffix = name.match(/\d+$/)?.[0];
  if (numericSuffix) return numericSuffix.padStart(2, "0");

  return name
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function TestimonialsColumn({
  className = "",
  testimonials,
  duration = 16,
  paused = false,
}: TestimonialsColumnProps) {
  const [scope, animate] = useAnimate<HTMLDivElement>();
  const playbackRef = useRef<PlaybackControls | null>(null);
  const reducedMotion = useReducedMotion();
  const inView = useInView(scope, { amount: 0.1 });

  useEffect(() => {
    if (reducedMotion || testimonials.length === 0 || !scope.current) return;

    const playback = animate(
      scope.current,
      { translateY: "-50%" },
      {
        duration,
        repeat: Infinity,
        ease: "linear",
        repeatType: "loop",
      },
    );
    playbackRef.current = playback;
    playback.pause();

    return () => {
      playback.cancel();
      playbackRef.current = null;
    };
  }, [animate, duration, reducedMotion, scope, testimonials.length]);

  useEffect(() => {
    const playback = playbackRef.current;
    if (!playback || reducedMotion) return;

    if (paused || !inView) playback.pause();
    else playback.play();
  }, [inView, paused, reducedMotion]);

  const passes = reducedMotion ? [0] : [0, 1];

  return (
    <div className={`testimonials-column ${className}`.trim()}>
      <motion.div className="testimonials-column__track" ref={scope}>
        {passes.map((pass) => (
          <div
            aria-hidden={pass === 1 ? "true" : undefined}
            className="testimonials-column__pass"
            key={pass}
          >
            {testimonials.map(({ text, image, name, role }, index) => (
              <article className="testimonial-card" key={`${pass}-${name}-${index}`}>
                <p>{text}</p>
                <footer>
                  {image ? (
                    <img alt="" height={40} loading="lazy" src={image} width={40} />
                  ) : (
                    <span aria-hidden="true" className="testimonial-card__profile-mark">
                      {profileMark(name)}
                    </span>
                  )}
                  <span className="testimonial-card__identity">
                    <strong>{name}</strong>
                    <span>{role}</span>
                  </span>
                </footer>
              </article>
            ))}
          </div>
        ))}
      </motion.div>
    </div>
  );
}
